import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { FieldBehaviorMode } from '@app/queryTyping';

import { formControlCustomRule, formControlPatternRule } from '@app/common/components/controls/formControlValidation';
import { isNotEmptyString } from '@app/common/utils';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { MaskedTextFieldFragment } from '../../queries/queryTyping/all-fields';
import { FieldComponent, FieldLocator } from '../../types';
import { combineNames, getFieldErrorMessage, isZipCodeField } from '../../utils';

import { ZipCodeInput } from './ZipCodeInput';

const zipFormat = (val: string) => {
  if (val.length > 5) {
    const first = val.substring(0, 5);
    const last = val.substring(5, val.length);
    return `${first}-${last}`;
  }

  return val;
};

export const ZipCodeFieldComponent: FieldComponent<MaskedTextFieldFragment> = ({
  parent,
  field,
  fieldProps,
}) => {
  const { t } = useTranslation();
  const name = combineNames(parent, field.id);

  const {
    formState: { errors },
  } = useFormContext();
  const required = field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED;

  const commonValidationMessages = useCommonValidationMessages();
  const getErrorMessage = useCallback(
    () => getFieldErrorMessage(name, errors, commonValidationMessages),
    [name, errors],
  );

  const validationRules = useMemo(() => [
    formControlCustomRule(
      // eslint-disable-next-line i18next/no-literal-string
      'required',
      (value) => {
        if (required && !isNotEmptyString(value, false)) {
          return t(
            'zip-code-field.validation.required|Validation message for zip field required value',
            'This field is required',
          );
        }

        return true;
      },
    ),
    formControlPatternRule(
      /^[0-9]{5}(?:-[0-9]{4})?$/,
      t(
        'zip-code-field.validation.pattern|Zip code input validation message',
        'Please enter your five or nine digits zip code',
      ),
    ),
  ], [required]);

  return (
    <ZipCodeInput
      name={name}
      format={zipFormat}
      minLength={field.minLength}
      maxLength={10}
      label={field.label || ''}
      data-stable-name="ZIPCodeInput"
      defaultValue={field.sValue ?? ''}
      validationRules={validationRules}
      errorMessage={getErrorMessage()}
      {...fieldProps}
    />
  );
};

export const ZipCodeFieldLocator: FieldLocator = ({ field }) => (
  isZipCodeField(field)
    ? ZipCodeFieldComponent
    : undefined
);
